/**
 * iter318 wave 16 — VyomOS "Find my academy" login helper.
 *
 * Because Emergent binds one custom domain per deployment, we replace
 * the standalone `my.vyomos.io` service with an in-app helper that
 * lives at `/login`. Users enter their academy's subdomain slug and
 * are redirected to `<slug>.vyomos.io` where the tenant's own login
 * screen takes over.
 *
 * All "Login" / "Customer Login" CTAs across the marketing site now
 * point here.
 */
import { useState } from 'react';
import { useNoIndex } from './usePageSeo';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LogIn, Search, HelpCircle, Mail, Lock } from 'lucide-react';
import { GoogleSignInButton, useGoogleAuthCallback } from '../../../components/GoogleSignIn';

const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL);

// Only allow characters valid in a DNS label so we can't produce a
// bogus URL from raw pasted input.
const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

// iter318 wave 18 — Shared redirect helper. Turns a handoff_token +
// tenant_slug into the tenant subdomain URL that will complete login.
// iter318 wave 21 — Super-admin choices carry `is_super_admin: true`
// and their handoff_token is bound to the "__superadmin__" slug. We
// keep them on `www.vyomos.io` and route through the platform-scope
// handoff endpoint (see `/auth/super-admin-continue`).
function forwardToTenant(choice) {
  if (choice.is_super_admin) {
    window.location.href = `/auth/super-admin-continue?token=${encodeURIComponent(choice.handoff_token)}`;
    return;
  }
  const host = choice.tenant_host || `${choice.tenant_slug}.vyomos.io`;
  window.location.href = `https://${host}/auth/google-continue?token=${encodeURIComponent(choice.handoff_token)}`;
}

export default function VyomosLogin() {
  useNoIndex();
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');
  const [googleChoices, setGoogleChoices] = useState(null); // { email, choices: [...] }

  // Email/password (global) state
  const [mode, setMode] = useState('google'); // 'google' | 'email'
  const [pwEmail, setPwEmail] = useState('');
  const [pwPassword, setPwPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState('');

  // iter318 wave 19 — Global "Forgot password?" state.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const { processing: googleProcessing, error: googleError } = useGoogleAuthCallback({
    mode: 'global',
    onGlobalChoices: (data) => {
      if (data.choices.length === 1) forwardToTenant(data.choices[0]);
      else setGoogleChoices(data);
    },
  });

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    setPwError('');
    try {
      const { data } = await axios.post(
        `${API}/api/auth/global/password-login`,
        { email: pwEmail, password: pwPassword },
      );
      if (data.choices.length === 1) forwardToTenant(data.choices[0]);
      else setGoogleChoices(data); // reuse the same picker for email flow
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Sign-in failed. Please try again.');
    } finally {
      setPwBusy(false);
    }
  };

  // iter318 wave 19 — Cross-tenant forgot-password. Always resolves as
  // "email sent if the account exists" so we don't leak tenant membership.
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotBusy(true);
    setForgotError('');
    try {
      await axios.post(`${API}/api/auth/global/forgot-password`, {
        email: forgotEmail.trim(),
      });
      setForgotSent(true);
    } catch (err) {
      // Even on network error we prefer the safe generic message.
      setForgotError(
        err.response?.data?.detail
          || 'Something went wrong. Please try again in a moment.',
      );
    } finally {
      setForgotBusy(false);
    }
  };

  const openForgot = () => {
    setForgotOpen(true);
    setForgotSent(false);
    setForgotError('');
    setForgotEmail(pwEmail || '');
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotSent(false);
    setForgotError('');
  };

  const normalize = (raw) => {
    // Accept:  "dascricket", "dascricket.vyomos.io",
    //          "https://dascricket.vyomos.io", "https://dascricket.vyomos.io/login"
    let s = (raw || '').trim().toLowerCase();
    s = s.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (s.endsWith('.vyomos.io')) s = s.slice(0, -'.vyomos.io'.length);
    return s;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Enter inside the email/password fields signs in; it must not trigger the subdomain lookup.
    const inEmailForm = document.activeElement?.closest?.('[data-testid="login-email-form-wrapper"]');
    if (mode === 'email' && (inEmailForm || !slug.trim())) {
      if (pwEmail && pwPassword && !pwBusy) handleEmailLogin(e);
      return;
    }
    const clean = normalize(slug);
    if (!clean || !SLUG_RE.test(clean)) {
      setError('Enter a valid academy subdomain (letters, numbers, and hyphens).');
      return;
    }
    if (clean === 'www' || clean === 'my' || clean === 'vyomos') {
      setError('That looks like a system subdomain, not an academy one.');
      return;
    }
    window.location.href = `https://${clean}.vyomos.io`;
  };

  return (
    <div data-testid="vyomos-login">
      <section className="bg-gradient-to-b from-[#EAF5FF] via-[#F8FBFF] to-white">
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#DCEEFF] text-[#1677FF] text-xs font-medium mb-4">
            <LogIn className="h-3 w-3" /> Sign in to VyomOS
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            Find your academy.
          </h1>
          <p className="text-[#566579] max-w-md mx-auto">
            Enter your academy&apos;s VyomOS subdomain and we&apos;ll take you to your login screen.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-xl mx-auto px-6 py-10">
          <form
            onSubmit={handleSubmit}
            data-testid="login-slug-form"
            className="bg-white border border-[#DCE6F0] rounded-3xl p-8 shadow-sm"
          >
            {googleChoices ? (
              <TenantPicker data={googleChoices} onBack={() => setGoogleChoices(null)} />
            ) : forgotOpen ? (
              <ForgotPasswordPanel
                email={forgotEmail}
                setEmail={setForgotEmail}
                onSubmit={handleForgotSubmit}
                onBack={closeForgot}
                busy={forgotBusy}
                sent={forgotSent}
                error={forgotError}
              />
            ) : (
              <>
                {/* Google / Email mode toggle */}
                <div className="flex gap-2 mb-5 bg-[#F8FBFF] p-1 rounded-full border border-[#DCE6F0]" data-testid="login-mode-tabs">
                  <button
                    type="button"
                    onClick={() => setMode('google')}
                    data-testid="login-mode-google"
                    className={mode === 'google'
                      ? 'flex-1 px-4 py-2 rounded-full bg-white text-[#10233F] font-semibold text-sm shadow-sm'
                      : 'flex-1 px-4 py-2 rounded-full text-[#566579] hover:text-[#10233F] text-sm'}
                  >Google</button>
                  <button
                    type="button"
                    onClick={() => setMode('email')}
                    data-testid="login-mode-email"
                    className={mode === 'email'
                      ? 'flex-1 px-4 py-2 rounded-full bg-white text-[#10233F] font-semibold text-sm shadow-sm'
                      : 'flex-1 px-4 py-2 rounded-full text-[#566579] hover:text-[#10233F] text-sm'}
                  >Email &amp; password</button>
                </div>

                {mode === 'google' && (
                  <>
                    <GoogleSignInButton returnPath="/login" dataTestId="login-google-btn" />
                    {(googleProcessing || googleError) && (
                      <p className={googleError ? 'mt-3 text-sm text-rose-600' : 'mt-3 text-sm text-[#566579]'} data-testid="login-google-status">
                        {googleError || 'Verifying your Google account…'}
                      </p>
                    )}
                  </>
                )}

                {mode === 'email' && (
                  <div data-testid="login-email-form-wrapper">
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-[#10233F] mb-1">Email</label>
                        <div className="flex items-center gap-2 border border-[#DCE6F0] rounded-xl px-3 focus-within:border-[#1677FF] focus-within:ring-4 focus-within:ring-[#1677FF]/10">
                          <Mail className="h-4 w-4 text-[#566579]" />
                          <input
                            type="email" required autoFocus
                            value={pwEmail}
                            onChange={(e) => { setPwEmail(e.target.value); setPwError(''); }}
                            data-testid="login-email-input"
                            className="flex-1 py-2.5 outline-none text-[#10233F] placeholder-[#96A3B4]"
                            placeholder="you@example.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-[#10233F] mb-1">Password</label>
                        <div className="flex items-center gap-2 border border-[#DCE6F0] rounded-xl px-3 focus-within:border-[#1677FF] focus-within:ring-4 focus-within:ring-[#1677FF]/10">
                          <Lock className="h-4 w-4 text-[#566579]" />
                          <input
                            type="password" required
                            value={pwPassword}
                            onChange={(e) => { setPwPassword(e.target.value); setPwError(''); }}
                            data-testid="login-password-input"
                            className="flex-1 py-2.5 outline-none text-[#10233F]"
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                    </div>
                    {pwError && (
                      <p className="text-sm text-rose-600 mb-3" data-testid="login-email-error">{pwError}</p>
                    )}
                    <button
                      type="button"
                      onClick={handleEmailLogin}
                      disabled={pwBusy || !pwEmail || !pwPassword}
                      data-testid="login-email-submit"
                      className="w-full px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] disabled:opacity-60 text-white font-semibold transition"
                    >
                      {pwBusy ? 'Signing in…' : 'Sign in'}
                    </button>
                    <div className="mt-3 text-right">
                      <button
                        type="button"
                        onClick={openForgot}
                        data-testid="login-forgot-password-link"
                        className="text-sm text-[#1677FF] hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-[#DCE6F0]" />
                  <span className="text-xs text-[#566579] uppercase tracking-wider">or find by subdomain</span>
                  <div className="flex-1 h-px bg-[#DCE6F0]" />
                </div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#10233F] mb-2">
                  Your academy subdomain
                </label>

            <div className="flex items-stretch rounded-xl border border-[#DCE6F0] overflow-hidden focus-within:border-[#1677FF] focus-within:ring-4 focus-within:ring-[#1677FF]/10 transition">
              <div className="flex items-center pl-3 pr-1 text-[#566579]">
                <Search className="h-4 w-4" />
              </div>
              <input
                autoFocus={mode !== 'email'}
                value={slug}
                onChange={(e) => { setSlug(e.target.value); setError(''); }}
                placeholder="dascricket"
                data-testid="login-slug-input"
                className="flex-1 px-2 py-3 text-base outline-none text-[#10233F] placeholder-[#96A3B4]"
              />
              <div className="flex items-center px-4 bg-[#F8FBFF] text-[#566579] text-sm border-l border-[#DCE6F0]">
                .vyomos.io
              </div>
            </div>

            {error && (
              <p className="mt-3 text-sm text-rose-600" data-testid="login-slug-error">{error}</p>
            )}

            <button
              type="submit"
              data-testid="login-slug-submit"
              className="mt-6 w-full px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold transition"
            >
              Continue to your academy →
            </button>

            <div className="mt-6 pt-6 border-t border-[#DCE6F0] text-sm text-[#566579] flex items-start gap-2">
              <HelpCircle className="h-4 w-4 mt-0.5 text-[#1677FF] flex-shrink-0" />
              <div>
                <span className="text-[#10233F] font-medium">Don&apos;t know your subdomain? </span>
                Check the welcome email your academy sent you, or{' '}
                <Link to="/contactus" className="text-[#1677FF] hover:underline">contact our team</Link>.
              </div>
            </div>
              </>
            )}
          </form>

          <div className="text-center mt-6 text-sm text-[#566579]">
            New to VyomOS?{' '}
            <Link to="/demovyomos" className="text-[#1677FF] hover:underline font-medium">
              Try a live demo →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function TenantPicker({ data, onBack }) {
  return (
    <div data-testid="google-tenant-picker">
      <h3 className="text-lg font-bold text-[#10233F] mb-1">Welcome back!</h3>
      <p className="text-sm text-[#566579] mb-5">
        We found <strong>{data.choices.length}</strong> academies linked to <strong>{data.email}</strong>. Pick which one you&apos;d like to enter:
      </p>
      <div className="space-y-2 mb-4">
        {data.choices.map((c) => {
          const key = c.is_super_admin ? '__superadmin__' : c.tenant_slug;
          const href = c.is_super_admin
            ? `/auth/super-admin-continue?token=${encodeURIComponent(c.handoff_token)}`
            : `https://${c.tenant_slug}.vyomos.io/auth/google-continue?token=${encodeURIComponent(c.handoff_token)}`;
          const subLine = c.is_super_admin ? 'vyomos.io/super-admin' : `${c.tenant_slug}.vyomos.io`;
          return (
            <a
              key={key}
              href={href}
              data-testid={`google-choice-${key}`}
              className="flex items-center justify-between px-4 py-3 rounded-2xl border border-[#DCE6F0] hover:border-[#1677FF] hover:bg-[#F8FBFF] transition"
            >
              <div>
                <div className="font-semibold text-[#10233F]">{c.tenant_name}</div>
                <div className="text-xs text-[#566579]">{subLine}</div>
              </div>
              <span className="text-[#1677FF] font-medium text-sm">Continue →</span>
            </a>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-[#566579] hover:text-[#10233F]"
        data-testid="google-picker-back"
      >
        ← Use a different sign-in method
      </button>
    </div>
  );
}

function ForgotPasswordPanel({ email, setEmail, onSubmit, onBack, busy, sent, error }) {
  if (sent) {
    return (
      <div data-testid="forgot-password-sent">
        <h3 className="text-lg font-bold text-[#10233F] mb-2">Check your inbox</h3>
        <p className="text-sm text-[#566579] mb-6">
          If an account exists for <strong>{email}</strong>, we&apos;ve sent a
          password-reset link. Follow it to set a new password on your academy&apos;s
          site. The link expires in one hour.
        </p>
        <p className="text-xs text-[#96A3B4] mb-6">
          Have accounts on more than one academy? You&apos;ll receive one email per
          academy — pick whichever you meant to reset.
        </p>
        <button
          type="button"
          onClick={onBack}
          data-testid="forgot-password-back-to-login"
          className="text-sm text-[#1677FF] hover:underline"
        >
          ← Back to sign in
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={onSubmit} data-testid="forgot-password-form">
      <h3 className="text-lg font-bold text-[#10233F] mb-1">Reset your password</h3>
      <p className="text-sm text-[#566579] mb-5">
        Enter the email you use to sign in. We&apos;ll email you a secure reset
        link scoped to your academy.
      </p>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#10233F] mb-1">
        Email
      </label>
      <div className="flex items-center gap-2 border border-[#DCE6F0] rounded-xl px-3 focus-within:border-[#1677FF] focus-within:ring-4 focus-within:ring-[#1677FF]/10 mb-4">
        <Mail className="h-4 w-4 text-[#566579]" />
        <input
          type="email" required autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="forgot-password-email-input"
          className="flex-1 py-2.5 outline-none text-[#10233F] placeholder-[#96A3B4]"
          placeholder="you@example.com"
        />
      </div>
      {error && (
        <p className="text-sm text-rose-600 mb-3" data-testid="forgot-password-error">{error}</p>
      )}
      <button
        type="submit"
        disabled={busy || !email.trim()}
        data-testid="forgot-password-submit"
        className="w-full px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] disabled:opacity-60 text-white font-semibold transition"
      >
        {busy ? 'Sending…' : 'Send reset link'}
      </button>
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          data-testid="forgot-password-back"
          className="text-sm text-[#566579] hover:text-[#10233F]"
        >
          ← Back to sign in
        </button>
      </div>
    </form>
  );
}


